import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { LineService } from "@services/line.service";
import { Line } from "@models/line.model";
import { PartNumbersService } from "@services/part-numbers.service";
import { PartNumber } from "@models/partNumber.model";
import { Group } from "@app/tool/model/group.model";
import { TableConfig } from "@app/shared/util/table-config.utils";
import { ToolService } from "@app/core/services/tool.service";
import { ToolRequestsService } from "@app/core/services/tool-requests.service";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import { ToolSet } from "@app/tool/model/toolSet.model";
import { Log } from "ng2-logger";
import { TranslateService } from "@ngx-translate/core";
import { Tool } from "@app/tool/model/tool.model";
import { InsertionCycle } from "@models/insertion-cycle.model";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { InsertionCyclesService } from "@services/insertion-cycles.service";
import { ObservableService } from '@app/core/services/observable.service';
import { Subscription } from 'rxjs';
import { LogoutModalComponent } from '@app/shared/components';
import { environment } from '../../../../../../environments/environment';

class ToolSteps {
    static SELECT_LINE_AND_NUMBER = 0;
    static SELECT_TOOLS = 1;
    static CONFIRM = 2;
}

@Component({
    selector: 'app-tool-group-request',
    templateUrl: './tool-group-request.component.html',
    styleUrls: ['./tool-group-request.component.scss']
})
export class ToolGroupRequestComponent implements OnInit, OnDestroy, AfterViewInit {
    _logger = Log.create(ToolGroupRequestComponent.name);

    @ViewChild('successModal', {static: true}) confirmationModal: ConfirmationModalComponent;
    @ViewChild('errorModal', {static: true}) errorModal: ConfirmationModalComponent;
    @ViewChild(LogoutModalComponent, {static: false}) logoutModal: LogoutModalComponent;

    firstNextTried = false;

    currentStep = 1;

    lines: Line[] = [];
    selectedLine: Line;

    insertionCycles: InsertionCycle[] = [];
    selectedInsertionCycle: InsertionCycle;

    partNumbers: PartNumber[] = [];
    selectedPartNumber: PartNumber;

    isLoadingTools = false;
    tableConfig = new TableConfig();
    toolsGrouped = new Array<Group>();
    selectedTools: Array<Tool | ToolSet> = new Array<Tool | ToolSet>();
    toolsToSwitch: Array<Tool | ToolSet> = new Array<Tool | ToolSet>();
    toolsFromAtom: Array<Tool | ToolSet> = new Array<Tool | ToolSet>();

    toolRequestForm: UntypedFormGroup;
    confirmationPopupMessage: string;
    isError: boolean;
    isConfirmationInProgress: boolean;
    hasGiveConsent = false;

    orderWithPriority = false;
    observableServiceSubscription: Subscription;
    readonly badgeLogoutCountdownSeconds = environment.badgeLogoutCountdownSeconds;

    constructor(private lineService: LineService,
                private insertionCyclesService: InsertionCyclesService,
                private partNumberService: PartNumbersService,
                private toolService: ToolService,
                private cdr: ChangeDetectorRef,
                private toolRequestService: ToolRequestsService,
                private translateService: TranslateService,
                observableService: ObservableService) {
        this.observableServiceSubscription = observableService.hasChanged$.subscribe(
            () => {
                if (this.currentStep === 2) {
                    this.onStepChange(ToolSteps.SELECT_TOOLS);
                }
            }
        )
    }

    ngOnInit() {
        this._logger.info("ngOnInit:: Retrieving lines");
        this.isLoadingTools = true;

        this.toolRequestForm = new UntypedFormGroup({
            line: new UntypedFormControl(),
            partNumber: new UntypedFormControl(),
            insertionCycle: new UntypedFormControl(),
        });

        this.lineService.getAllLines().subscribe(
            (data: any) => {
                this._logger.info("ngOnInit:: API Returned data", data);
                this.lines = data.items;
                this.isLoadingTools = false;
            },
            (error: any) => {
                this._logger.error("ngOnInit:: API Returned error", error);
                this.isLoadingTools = false;
            }
        );
    }

    ngOnDestroy() {
        this.observableServiceSubscription.unsubscribe();
    }

    ngAfterViewInit() {
        this.cdr.markForCheck();
    }

    onStepChange(stepIndex) {
        this.firstNextTried = false;
        switch (stepIndex) {
            case ToolSteps.SELECT_LINE_AND_NUMBER:
                this.currentStep = 1;
                break;
            case ToolSteps.SELECT_TOOLS:
                this.selectedTools = new Array<Tool | ToolSet>();
                this.getToolsForLinePartNumberAndInsertionCycle(this.tableConfig,
                    this.selectedLine.id, this.selectedPartNumber.id,
                    this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null, true);
                this.currentStep = 2;
                break;
            case ToolSteps.CONFIRM:
                this.getToolsThatNeedLineSwitch();
                this.currentStep = 3;
                break;
        }
    }

    onPageChange(tableState) {
        this._logger.info('onPageChange:: event not required');
    }

    onSortChange(tableState) {
        this.getToolsForLinePartNumberAndInsertionCycle(tableState, this.selectedLine.id,
            this.selectedPartNumber.id, this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null);
    }

    onFilterChange(tableState) {
        this.getToolsForLinePartNumberAndInsertionCycle(tableState, this.selectedLine.id,
            this.selectedPartNumber.id, this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null, true);
    }

    onGroupChange(tableState) {
        this.getToolsForLinePartNumberAndInsertionCycle(tableState, this.selectedLine.id,
            this.selectedPartNumber.id, this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null);
    }

    onLineSelected(line: Line) {
        if (line && line instanceof Line &&
            ((this.selectedLine && this.selectedLine.id !== line.id) || !this.selectedLine)
        ) {
            this._logger.info("onLineSelected:: Retrieving part numbers for line", line);
            this.selectedLine = line;
            this.toolRequestForm.controls['partNumber'].setValue(null);
            this.toolRequestForm.controls['insertionCycle'].setValue(null);
            this.partNumbers = [];
            this.selectedPartNumber = null;
            this.selectedInsertionCycle = null;
            this.toolsGrouped = new Array<Group>();
            this.selectedTools = new Array<Tool | ToolSet>();
            this.partNumberService.getPartNumbersForLine(this.selectedLine.id).subscribe(
                (data: any) => {
                    this._logger.info("onLineSelected:: API Returned data", data);
                    this.partNumbers = data.partNumbers;
                },
                (error: any) => {
                    this._logger.error("onLineSelected:: API Returned error", error);
                }
            );
        }
    }

    onPartNumberSelected(partNumber: PartNumber) {
        if (partNumber && partNumber instanceof PartNumber) {
            this._logger.info("onPartNumberSelected:: ", partNumber);
            this.selectedPartNumber = partNumber;
            if (this.currentStep === 1) {
                this.insertionCycles = [];
                this.selectedInsertionCycle = null;
                this.toolsGrouped = new Array<Group>();
                this.selectedTools = new Array<Tool | ToolSet>();
                this.toolRequestForm.controls['insertionCycle'].setValue(null);
            }
            this.getInsertionCyclesFromLineAndPartNumber(this.selectedLine.id, this.selectedPartNumber.id);
        }
    }

    onInsertionCycleSelect(insertionCycle: InsertionCycle) {
        if (insertionCycle) {
            this.selectedInsertionCycle = insertionCycle;
        }
    }

    onToolClicked(toolDetails: { checked: boolean, group: Tool | ToolSet }) {
        if (toolDetails.checked) {
            this.selectedTools.push(toolDetails.group);
        } else {
            const index = this.selectedTools.findIndex(selectedTool => selectedTool.id === toolDetails.group.id);
            if (index !== -1) {
                this.selectedTools.splice(index, 1);
            }
        }
        this._logger.info("onToolClicked:: Selected tools", this.selectedTools);
    }

    onTryNext() {
        this.firstNextTried = true;
    }

    onOrder(priority: boolean) {
        this.orderWithPriority = priority;
        this.confirmationModal.open();
    }

    getIdsArrayForSelectedTools(selectedTools: Array<Tool | ToolSet>) {
        const ids = [];
        for (const toolItem of selectedTools) {
            if (toolItem instanceof ToolSet) {
                for (const tool of toolItem.tools) {
                    ids.push(tool['id']);
                }
            } else {
                ids.push(toolItem['id']);
            }
        }
        return ids;
    }

    onConfirmOrder(reason) {
        this.isError = false;
        this.isConfirmationInProgress = true;
        this._logger.info("onConfirmOrder:: Creating tool request, Reason:: " + reason);
        this.toolRequestService.createToolRequest(
            this.selectedLine.id,
            this.selectedPartNumber.id,
            this.orderWithPriority,
            reason,
            this.getIdsArrayForSelectedTools(this.selectedTools),
            this.hasGiveConsent,
            this.selectedInsertionCycle ? this.selectedInsertionCycle.id : null
        ).subscribe(
            data => {
                this.confirmationModal.close();
                this._logger.info("onConfirmOrder:: API returned data", data);

                if (data.status === 'Closed') {
                    this.confirmationPopupMessage = this.translateService.instant('lineSetter.toolRequest.toasts.toolRequestCreatedClosed');
                } else {
                    this.confirmationPopupMessage = this.translateService.instant('lineSetter.toolRequest.toasts.toolRequestCreated');
                }

                this.isConfirmationInProgress = false;
                this.logoutModal.open();
            },
            error => {
                this.confirmationModal.close();
                if (error && error.error) {
                    this.confirmationPopupMessage = this.translateService.instant(error.error.message);
                    this.isError = true;
                    this.errorModal.open();
                }
                this.isConfirmationInProgress = false;
                this._logger.error("onConfirmOrder:: API returned error", error);
            }
        )
    }

    private getToolsForLinePartNumberAndInsertionCycle(tableConfig = this.tableConfig,
                                                       lineId: number, partNumberId: number,
                                                       insertionCycleId: number = null, selectTools: boolean = false) {
        this.isLoadingTools = true;
        this.toolsGrouped = [];

        this._logger.info(`getToolsForLineAndPartNumberAndSelect::
            Retrieving tools for (tableConfig,lineID,partNumberID, insertionCycleId)`,
            tableConfig, lineId, partNumberId, insertionCycleId);

        this.tableConfig.upsertFilter('isChangeOver', true);
        // pagination removed
        this.tableConfig.pager.pageSize = 0;

        this.toolService.getToolsForLineAndPartNumber(
            this.tableConfig,
            lineId,
            partNumberId,
            true,
            insertionCycleId
        ).subscribe(data => {
            this._logger.info("getToolsForLineAndPartNumberAndSelect:: Api return data", data);
            this.toolsGrouped = data.tools;
            this.tableConfig.pager.count = data.count;
            if (selectTools) {
                this.selectedTools = new Array<Tool | ToolSet>();
                data.tools.forEach((tool: Tool | ToolSet) => {
                    if (tool['isAlreadyAtLine']) {
                        tool['disabled'] = tool['isAlreadyAtLine'];
                    } else {
                        tool['checked'] = true;
                        tool['selected'] = true;
                        this.selectedTools.push(tool);
                    }
                });
            }
            this.isLoadingTools = false;
        });
    }

    private getInsertionCyclesFromLineAndPartNumber(lineId: number, partNumberId: number) {
        this.isLoadingTools = true;
        this._logger.info("getInsertionCyclesFromLineAndPartNumber:: Selecting insertion cycles for (lineID, partNumberId)",
            lineId, partNumberId);
        const config = new TableConfig();
        config.filters = [
            {field: 'lineIds', value: [lineId]},
            {field: 'partNumbersIds', value: [partNumberId]}
        ];

        this.insertionCyclesService.getInsertionCycles(config)
            .subscribe((data: any) => {
                this._logger.info("getInsertionCyclesFromLineAndPartNumber:: Api return data", data);
                this.insertionCycles = data.items;
                this.isLoadingTools = false;
            });
    }

    private getToolsThatNeedLineSwitch() {
        const ids = this.selectedTools.map(x => x.id);
        this.isLoadingTools = true;
        this.toolsToSwitch = [];
        this.toolsFromAtom = [];
        this.hasGiveConsent = false;
        this.selectedTools.forEach(x => this.toolsFromAtom.push(x));
        this.toolService.getToolsThatNeedLineSwitch(ids, this.selectedLine.id).subscribe((data: any) => {
                if (data && data.length === 0) {
                    this.hasGiveConsent = true;
                    this.isLoadingTools = false;
                    return;
                }

                for (let i = 0; i < data.length; i++) {
                    const tool = this.selectedTools.find(x => x.id === data[i]);
                    if (!tool) {
                        continue;
                    }
                    const index = this.toolsFromAtom.indexOf(tool);
                    if (index >= 0) {
                        this.toolsFromAtom.splice(index, 1);
                    }
                    this.toolsToSwitch.push(tool);
                }


                this.isLoadingTools = false;
            },
            (error: any) => {
                this.isLoadingTools = false;
            });
    }

    canContinue() {
        if (!this.selectedLine || !this.selectedPartNumber) {
            return false;
        }
        return !(this.selectedLine.isSPI && !this.selectedInsertionCycle);
    }
}
